import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { t } from "i18next";
import { localized, useAppLanguage } from "../../../utils/localized";

import { custonSooner } from "../../../components/CustopmSooner";
import { Button } from "../../../components/ui/button";
import { Separator } from "../../../components/ui/separator";
import { Minus } from "../../../assets/Minus";
import { PlusIcon } from "../../../assets/Plus";
import { cn } from "../../../lib/utils";
import { useTelegramBackButton } from "../../../utils/BackButton";
import { useFooterTopSlot } from "../../../layout/main";
import { formatPrice } from "../../../utils/getPrice";
import { impactOccuer } from "../../../utils/hapticEvents";
import { useGetNearesBranch } from "../../addresses/services/getNearBranch";
import { useAddToBasket } from "../../basket/services/addToBasket";
import { useCartLimitReached } from "../../basket/services/useCartLimit";
import { useDecreaseFromBasket } from "../../basket/services/decreaseFromBasket";
import { useGetMenu } from "../../home/services/getMenu";
import { findProductInMenu } from "../../home/libs/menuLookup";
import { IModifierGroup, IModifierItem } from "../../home/types/Menu";
import {
  applyGroupSelection,
  buildCartKey,
  buildModifierPayload,
  getChoosableGroups,
  isRemovalGroup,
  getDefaultOrder,
  getDefaultSelections,
  getGroupId,
  getGroupLimit,
  getGroupMax,
  getGroupSelectedCount,
  getItemPrice,
  getModifierTotal,
  isSingleChoiceGroup,
  ModifierOrder,
  resolveMaxQuantity,
  ModifierState,
} from "../../home/libs/modifiers";
import { RemoveIngredients } from "./component/RemoveIngredients";
import { mindboxViewProduct } from "../../../lib/mindboxEvents";
import { useSelector } from "react-redux";
import { ChevronUp } from "lucide-react";
import { RootState } from "../../../store";
import { ProductImage } from "../../../components/ProductImage";

const FoodDetail = () => {
  useTelegramBackButton("/");

  const { addToBasket } = useAddToBasket();
  const { decreaseFromBasket } = useDecreaseFromBasket();
  const { id } = useParams();
  const navigate = useNavigate();
  const { nearest } = useGetNearesBranch();
  const { menu, isLoading } = useGetMenu(nearest?.branch_id);
  const lang = useAppLanguage();
  const detailProduct = findProductInMenu(menu, Number(id));

  // Меню приехало — значит про блюдо всё известно. Пустое меню (филиал ещё не
  // определился, запрос не стартовал) за ответ не считаем, иначе диплинк,
  // открытый холодным стартом, отбрасывало бы на главную ещё до загрузки.
  const isMenuReady = !isLoading && menu.length > 0;

  // Сюда приходят по диплинку — из баннера, сторис или ссылки извне, — и
  // блюда к этому моменту может уже не быть: удалили, сняли с продажи или его
  // не готовит ближайший филиал. Показываем это и возвращаем на главную.
  useEffect(() => {
    if (!isMenuReady || detailProduct) {
      return;
    }

    custonSooner.error(t("product-unavailable"));
    navigate("/", { replace: true });
  }, [isMenuReady, detailProduct, navigate]);

  // Просмотр карточки блюда. Ключ — id товара, а не факт монтирования: на
  // этом же экране открывают следующее блюдо из кросс-селла, без ремаунта.
  const viewedProductId = useRef<number | null>(null);

  useEffect(() => {
    if (!detailProduct || viewedProductId.current === detailProduct.id) return;

    viewedProductId.current = detailProduct.id;
    mindboxViewProduct(detailProduct);
  }, [detailProduct]);

  const modifierGroups = useMemo(
    () => detailProduct?.modifier_groups ?? [],
    [detailProduct],
  );

  const choosableGroups = useMemo(
    () => getChoosableGroups(modifierGroups),
    [modifierGroups],
  );

  // «Убрать ингредиенты» — те же модификаторы, но отдельным блоком с тегами,
  // и по макету он всегда идёт первым, до остальных групп.
  const visibleGroups = useMemo(
    () => choosableGroups.filter(({ group }) => !isRemovalGroup(group)),
    [choosableGroups],
  );

  const removalGroups = useMemo(
    () => choosableGroups.filter(({ group }) => isRemovalGroup(group)),
    [choosableGroups],
  );
  const [selectedModifiers, setSelectedModifiers] = useState<ModifierState>(
    () => getDefaultSelections(modifierGroups),
  );
  // Порядок выбора нужен для вытеснения по FIFO: в самих количествах его нет.
  const [selectionOrder, setSelectionOrder] = useState<ModifierOrder>(() =>
    getDefaultOrder(modifierGroups),
  );
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    setSelectedModifiers(getDefaultSelections(modifierGroups));
    setSelectionOrder(getDefaultOrder(modifierGroups));
  }, [detailProduct?.id, modifierGroups]);

  const cartKey = useMemo(
    () =>
      detailProduct ? buildCartKey(detailProduct.id, selectedModifiers) : "",
    [detailProduct, selectedModifiers],
  );

  const basketItem = useSelector((state: RootState) =>
    state.basket.items.find((item) => item.cartKey === cartKey),
  );

  const count = useMemo(() => basketItem?.quantity || 0, [basketItem]);
  const isLimitReached = useCartLimitReached(detailProduct);

  const selectedModifierPayload = useMemo(
    () => buildModifierPayload(modifierGroups, selectedModifiers),
    [modifierGroups, selectedModifiers],
  );

  const modifierTotal = useMemo(
    () => getModifierTotal(selectedModifierPayload),
    [selectedModifierPayload],
  );

  const finalPrice = (Number(detailProduct?.org_price) || 0) + modifierTotal;

  const isSelectionValid = useMemo(
    () =>
      modifierGroups.every((group, index) => {
        const min = group.restrictions?.minQuantity ?? 0;
        const max = getGroupMax(group);
        const groupId = getGroupId(group, index);
        const selectedCount = getGroupSelectedCount(selectedModifiers[groupId]);

        return selectedCount >= min && selectedCount <= max;
      }),
    [modifierGroups, selectedModifiers],
  );

  const updateQuantity = (
    group: IModifierGroup,
    groupIndex: number,
    item: IModifierItem,
    nextQuantity: number,
  ) => {
    const next = applyGroupSelection(
      { quantities: selectedModifiers, order: selectionOrder },
      group,
      groupIndex,
      item,
      nextQuantity,
    );

    setSelectedModifiers(next.quantities);
    setSelectionOrder(next.order);
  };

  const handleAdd = async (e: React.MouseEvent) => {
    e.stopPropagation();
    impactOccuer("light");

    if (detailProduct && isSelectionValid) {
      await addToBasket({
        product: detailProduct,
        cartKey,
        unitPrice: finalPrice,
        selectedModifiers: selectedModifierPayload,
      });
    }
  };

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    impactOccuer("light");

    if (detailProduct) {
      await decreaseFromBasket({
        product_id: detailProduct.id,
        cartKey,
        remove_all: false,
      });
    }
  };

  useFooterTopSlot(
    isLoading ? null : (
      // The footer's slot is deliberately unstyled, so the bar brings its own
      // gutters — `px-4` to line up with the page content, `pt-3`/`pb-3`
      // because the nav below drops its own padding whenever a slot is present.
      <div className="px-4 pt-3 pb-3">
        <div className="flex h-12 flex-row items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-body-base-medium text-text-primary">
              {formatPrice(finalPrice)} сум
            </p>
          </div>

          {count > 0 ? (
            <div className="relative flex h-10 min-w-[140px] items-center justify-between rounded-full bg-white px-3 text-text-primary">
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-full"
                style={{
                  padding: 1,
                  background:
                    "linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                  WebkitMask:
                    "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
                }}
              />
              <Button
                variant="link"
                onClick={handleRemove}
                className="h-8 w-8 rounded-full p-0 text-text-primary"
              >
                <Minus className="text-text-primary" />
              </Button>
              <span className="text-body-sm-semibold">{count}</span>
              <Button
                variant="link"
                onClick={handleAdd}
                aria-disabled={isLimitReached}
                className={cn("h-8 w-8 rounded-full p-0 text-text-primary", {
                  "opacity-40": isLimitReached,
                })}
              >
                <PlusIcon className="text-text-primary" />
              </Button>
            </div>
          ) : (
            <Button
              className="h-10 min-w-[140px] rounded-full bg-button-primary px-5 text-body-sm-semibold text-white"
              onClick={handleAdd}
              disabled={!isSelectionValid}
            >
              {t("addToCart")}
            </Button>
          )}
        </div>
      </div>
    ),
  );

  // Пока меню не приехало, показывать нечего: блюда ещё нет, но и уводить с
  // экрана рано — редирект выше сработает, когда меню действительно загрузится.
  if (isLoading || (!detailProduct && !isMenuReady)) {
    return (
      <div className="flex items-center justify-center h-full">Loading...</div>
    );
  }

  return (
    <div className="px-4 h-full flex-col space-y-3 overflow-y-auto pt-2 pb-28">
      <div className="w-full bg-white rounded-2xl overflow-hidden">
        <ProductImage
          // additional_image — отдельная картинка для детальной (её присылают
          // для авторских комбо). Нет поля или null — показываем обычную.
          src={detailProduct?.additional_image || detailProduct?.menu_image}
          alt="food"
          className={cn("w-full h-[261px] rounded-2xl", {
            // Дополнительная картинка снята под этот блок — растягиваем на всю
            // площадь. Обычная нарисована на своём фоне, её только вписываем.
            "object-cover": !!detailProduct?.additional_image,
            "object-contain": !detailProduct?.additional_image,
          })}
          placeholderClassName="p-16"
        />
      </div>

      <p className="text-display-lg">
        {localized(lang, {
          ru: detailProduct?.title,
          uz: detailProduct?.title_uz,
          en: detailProduct?.title_en,
        })}
      </p>

      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-col space-y-[2px]">
          {detailProduct?.org_price && (
            <p className="text-display-md font-inter-tight">
              {formatPrice(finalPrice)} сум
            </p>
          )}
        </div>
      </div>

      <Separator className="my-4" />

      {choosableGroups.length > 0 && (
        <div className="space-y-4">
          {detailProduct?.description && (
            <div className="pt-1">
              <p className="text-body-sm-regular">
                {localized(lang, {
                  ru: detailProduct?.description,
                  uz: detailProduct?.description_uz,
                  en: detailProduct?.description_en,
                })}
              </p>
            </div>
          )}

          {removalGroups.map(({ group, index: groupIndex }) => {
            const groupId = getGroupId(group, groupIndex);

            return (
              <RemoveIngredients
                key={groupId}
                group={group}
                selected={selectedModifiers[groupId]}
                collapsed={collapsedGroups[groupId] ?? false}
                onToggleCollapse={() =>
                  setCollapsedGroups((previous) => ({
                    ...previous,
                    [groupId]: !(previous[groupId] ?? false),
                  }))
                }
                // Тег-переключатель: исключён — количество 1, вернули — 0.
                onToggle={(item, excluded) =>
                  updateQuantity(group, groupIndex, item, excluded ? 0 : 1)
                }
              />
            );
          })}
          {visibleGroups.map(({ group, index: groupIndex }) => {
            const groupId = getGroupId(group, groupIndex);
            const groupTypeSingle = isSingleChoiceGroup(group);
            const groupMin = group.restrictions?.minQuantity ?? 0;
            const groupMax = getGroupMax(group);
            const groupFixed = groupMin > 0 && groupMin === groupMax;
            const isCollapsed = collapsedGroups[groupId] ?? false;
            const groupHint = group.description;
            // Лимит группы бывает меньше числа позиций в ней — без подписи
            // пользователь узнаёт о нём только когда выбор начнёт вытесняться.
            const groupLimit = getGroupLimit(group);

            return (
              <div
                key={groupId}
                className="overflow-hidden rounded-[24px] bg-white px-5 py-5"
              >
                <button
                  type="button"
                  onClick={() =>
                    setCollapsedGroups((previous) => ({
                      ...previous,
                      [groupId]: !isCollapsed,
                    }))
                  }
                  className="flex w-full items-start justify-between gap-4 text-left"
                >
                  <div className="space-y-1">
                    <p className="font-inter-tight text-[16px] font-semibold leading-[20px] text-[#0B0D0F]">
                      {group.name}
                    </p>
                    {groupHint && (
                      <p className="font-inter text-[10px] font-medium leading-[12px] text-[#878C98]">
                        {groupHint}
                      </p>
                    )}
                    {groupLimit != null && (
                      <p className="font-inter text-[10px] font-medium leading-[12px] text-[#878C98]">
                        {t("modifier-limit", { count: groupLimit })}
                      </p>
                    )}
                  </div>
                  <ChevronUp
                    className={cn(
                      "mt-1 h-5 w-5 shrink-0 text-text-tertiary transition-transform",
                      isCollapsed && "rotate-180",
                    )}
                  />
                </button>

                {!isCollapsed && (
                  <div className="mt-5 space-y-5">
                    {(group.items ?? []).map((item) => {
                      const quantity =
                        selectedModifiers[groupId]?.[item.itemId] ?? 0;
                      // Лимит группы позицию не блокирует: выбор сверх него
                      // гасит самый ранний из уже выбранных (см.
                      // applyGroupSelection), поэтому нажать можно всегда.
                      // Ноль остаётся только у позиций, которые бэк вовсе
                      // запретил (max_quantity: 0) — им «Добавить» не рисуем.
                      const canAdd =
                        groupMax > 0 &&
                        resolveMaxQuantity(item.restrictions?.maxQuantity) > 0;
                      const handleItemSelect = () => {
                        if (groupTypeSingle) {
                          updateQuantity(
                            group,
                            groupIndex,
                            item,
                            quantity > 0 ? 0 : 1,
                          );
                          return;
                        }

                        if (quantity === 0) {
                          updateQuantity(group, groupIndex, item, 1);
                        }
                      };

                      return (
                        <div
                          key={item.itemId}
                          className="flex min-h-14 items-center gap-3"
                        >
                          <ProductImage
                            src={item.buttonImageUrl}
                            alt={item.name}
                            loading="lazy"
                            className="h-14 w-14 shrink-0 rounded-xl object-contain"
                          />

                          <button
                            type="button"
                            onClick={handleItemSelect}
                            className="flex min-w-0 flex-1 flex-col items-start text-left"
                          >
                            <p className="text-display-sm leading-5">
                              {item.name}
                            </p>
                            <p className="text-body-sm-regular leading-5">
                              {getItemPrice(item) > 0
                                ? `+${formatPrice(getItemPrice(item))}`
                                : "0"}{" "}
                              сум
                            </p>
                          </button>

                          {groupTypeSingle ? (
                            <button
                              type="button"
                              onClick={handleItemSelect}
                              className={cn(
                                "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2",
                                quantity > 0
                                  ? "border-[#20C05C]"
                                  : "border-[#B2B8C6]",
                              )}
                            >
                              <span
                                className={cn(
                                  "h-5 w-5 rounded-full",
                                  quantity > 0
                                    ? "bg-[#20C05C]"
                                    : "bg-transparent",
                                )}
                              />
                            </button>
                          ) : quantity === 0 && canAdd ? (
                            // Счётчик с недоступным «−» на нуле не сообщает,
                            // что позицию вообще можно взять, — до первого
                            // выбора показываем обычную кнопку.
                            <button
                              type="button"
                              onClick={handleItemSelect}
                              className="flex h-8 shrink-0 items-center justify-center rounded-full bg-[#F0F2F3] px-4 text-body-sm-medium"
                              style={{ minWidth: "92px" }}
                            >
                              {t("add")}
                            </button>
                          ) : (
                            <div
                              className="flex h-8 shrink-0 items-center justify-center gap-3 rounded-full bg-[#F0F2F3] px-2.5"
                              style={{ minWidth: "92px" }}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  updateQuantity(
                                    group,
                                    groupIndex,
                                    item,
                                    quantity - 1,
                                  )
                                }
                                disabled={groupFixed || quantity <= 0}
                                className="flex h-5 w-5 items-center justify-center text-lg leading-none disabled:opacity-30"
                              >
                                −
                              </button>
                              <span className="min-w-4 text-center text-body-sm-medium">
                                {quantity}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  updateQuantity(
                                    group,
                                    groupIndex,
                                    item,
                                    quantity + 1,
                                  )
                                }
                                disabled={
                                  quantity >=
                                  resolveMaxQuantity(
                                    item.restrictions?.maxQuantity,
                                  )
                                }
                                className="flex h-5 w-5 items-center justify-center text-lg leading-none disabled:opacity-30"
                              >
                                +
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

        </div>
      )}

      {choosableGroups.length === 0 && (
        <div className="pt-2">
          <p className="text-body-sm-regular">
            {localized(lang, {
              ru: detailProduct?.description,
              uz: detailProduct?.description_uz,
              en: detailProduct?.description_en,
            })}
          </p>
        </div>
      )}
    </div>
  );
};

export default FoodDetail;
